// ============================================================================
// Rondines — store (API `patrols`, CC-CONSOLE #1592)
// ============================================================================

import { create } from "zustand";
import * as patrolsApi from "./services/patrolsApi";
import type { Checkpoint, PatrolDashboard, PatrolIncident, PatrolRound, PatrolRoute, PatrolSchedule } from "./services/patrolsApi";
import { apiErrorMessage } from "./services/basesApi";
import type { LoadState } from "./store";

interface PatrolsState {
  routes: PatrolRoute[];
  checkpoints: Checkpoint[];
  schedules: PatrolSchedule[];
  rounds: PatrolRound[];
  incidents: PatrolIncident[];
  dashboard: PatrolDashboard | null;
  status: LoadState;
  error: string | null;
  hydrate: () => Promise<void>;
  refreshDashboard: () => Promise<void>;
  saveRoute: (id: string | null, d: Partial<PatrolRoute>) => Promise<PatrolRoute>;
  removeRoute: (id: string) => Promise<void>;
  saveCheckpoint: (id: string | null, d: Partial<Checkpoint>, file?: File | null) => Promise<Checkpoint>;
  removeCheckpoint: (id: string) => Promise<void>;
  saveSchedule: (id: string | null, d: Partial<PatrolSchedule>) => Promise<PatrolSchedule>;
  removeSchedule: (id: string) => Promise<void>;
  saveRound: (id: string | null, d: Record<string, unknown>) => Promise<PatrolRound>;
  saveIncident: (id: string | null, d: Partial<PatrolIncident>, files?: File[]) => Promise<PatrolIncident>;
  removeIncident: (id: string) => Promise<void>;
}

const upsert = <T extends { id: string }>(list: T[], item: T) => (list.some((x) => x.id === item.id) ? list.map((x) => (x.id === item.id ? item : x)) : [item, ...list]);

/** Desde hace 7 días a las 00:00 (las rondas de la bitácora y del tablero). */
const weekAgoIso = () => new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();

export const usePatrolsStore = create<PatrolsState>((set, get) => ({
  routes: [],
  checkpoints: [],
  schedules: [],
  rounds: [],
  incidents: [],
  dashboard: null,
  status: "idle",
  error: null,

  hydrate: async () => {
    set({ status: get().status === "ready" ? "ready" : "loading", error: null });
    try {
      const [routes, checkpoints, schedules, rounds, incidents, dashboard] = await Promise.all([
        patrolsApi.listRoutes(),
        patrolsApi.listCheckpoints(),
        patrolsApi.listSchedules(),
        patrolsApi.listRounds({ from: weekAgoIso() }),
        patrolsApi.listIncidents(),
        patrolsApi.getDashboard().catch(() => null),
      ]);
      set({ routes, checkpoints, schedules, rounds, incidents, dashboard, status: "ready" });
    } catch (err) {
      set({ status: "error", error: apiErrorMessage(err, (err as Error)?.message || "error") });
    }
  },

  refreshDashboard: async () => {
    try {
      set({ dashboard: await patrolsApi.getDashboard() });
    } catch {
      /* opcional */
    }
  },

  saveRoute: async (id, d) => {
    const saved = id ? await patrolsApi.updateRoute(id, d) : await patrolsApi.createRoute(d);
    const count = get().checkpoints.filter((c) => c.route_id === saved.id).length;
    set({ routes: upsert(get().routes, { ...saved, checkpointCount: count }) });
    return saved;
  },
  removeRoute: async (id) => {
    await patrolsApi.deleteRoute(id);
    set({
      routes: get().routes.filter((r) => r.id !== id),
      checkpoints: get().checkpoints.filter((c) => c.route_id !== id),
      schedules: get().schedules.filter((s) => s.route_id !== id),
    });
    void get().refreshDashboard();
  },

  saveCheckpoint: async (id, d, file) => {
    const saved = id ? await patrolsApi.updateCheckpoint(id, d, file) : await patrolsApi.createCheckpoint(d, file);
    const checkpoints = upsert(get().checkpoints, saved);
    set({ checkpoints, routes: get().routes.map((r) => ({ ...r, checkpointCount: checkpoints.filter((c) => c.route_id === r.id).length })) });
    return saved;
  },
  removeCheckpoint: async (id) => {
    await patrolsApi.deleteCheckpoint(id);
    const checkpoints = get().checkpoints.filter((c) => c.id !== id);
    set({ checkpoints, routes: get().routes.map((r) => ({ ...r, checkpointCount: checkpoints.filter((c) => c.route_id === r.id).length })) });
  },

  saveSchedule: async (id, d) => {
    const saved = id ? await patrolsApi.updateSchedule(id, d) : await patrolsApi.createSchedule(d);
    set({ schedules: upsert(get().schedules, saved) });
    return saved;
  },
  removeSchedule: async (id) => {
    await patrolsApi.deleteSchedule(id);
    set({ schedules: get().schedules.filter((s) => s.id !== id) });
  },

  saveRound: async (id, d) => {
    const saved = id ? await patrolsApi.updateRound(id, d) : await patrolsApi.createRound(d);
    set({ rounds: upsert(get().rounds, saved) });
    void get().refreshDashboard();
    return saved;
  },

  saveIncident: async (id, d, files = []) => {
    const saved = id ? await patrolsApi.updateIncident(id, d, files) : await patrolsApi.createIncident(d, files);
    set({ incidents: upsert(get().incidents, saved) });
    void get().refreshDashboard();
    return saved;
  },
  removeIncident: async (id) => {
    await patrolsApi.deleteIncident(id);
    set({ incidents: get().incidents.filter((i) => i.id !== id) });
    void get().refreshDashboard();
  },
}));
